<template>
  <header id="setupHeader"
          class="setup-header"
          v-bind:class="{'prevent-click': preventClick}">
    <b-row no-gutters>
      <b-col cols="2">
        <b-button v-if="showMenu"
                  id="sidebarMenuButton"
                  class="button--icon"
                  :aria-label="$t('open_menu')"
                  aria-haspopup="menu"
                  aria-controls="sidebarMenu"
                  aria-expanded="false"
                  @click="openSidebar">
          <inline-svg class="icon--hamburger"
                      src="HamburgerIcon">
          </inline-svg>
        </b-button>

        <b-button v-else-if="showBack"
                  id="backButton"
                  class="button--icon back-button"
                  :aria-label="$t('go_back')"
                  @click="goBack">
          <b-row>
            <b-col align="left">
              <div id="backButtonImage"></div>
            </b-col>
          </b-row>
        </b-button>
      </b-col>

      <b-col cols="8"
             align="center"
             align-self="center">
        <h1 class="text-header text-20 text-400"
            :class="{ 'sr-only': screenReaderOnlyHeader}">
          {{ text }}
        </h1>
      </b-col>

      <b-col cols="2"></b-col>
    </b-row>
  </header>
</template>

<script>
// Components
import InlineSvg from './InlineSvg.vue'

// Libraries
import { mapState } from 'vuex'
import { MenuButtonEnum } from '../../../constants'
import { EventBus } from '../../../services/eventBusService'

// Utilities
import isEmpty from '../../../utility/isEmpty'

export default {
  name: 'setup-header',

  components: {
    InlineSvg
  },

  computed: {
    text () {
      return this.screenReaderOnlyHeader ? this.$store.state.display.ariaHeader : this.$store.state.display.header.text
    },

    buttonType () {
      return this.$store.state.display.header.buttonType
    },

    showMenu () {
      return this.buttonType === MenuButtonEnum.menu && !this.menuOpen
    },

    showBack () {
      return this.buttonType === MenuButtonEnum.back
    },

    menuOpen () {
      return this.$store.state.display.sidebarOpen
    },

    preventClick () {
      return this.$store.state.display.preventHeaderClick
    },

    currentRouteName () {
      return this.$route.name || ''
    },

    screenReaderOnlyHeader () {
      return isEmpty(this.$store.state.display.header.text)
    },

    ...mapState('payment', ['disbursementAmount'])
  },

  methods: {
    goBack () {
      EventBus.$emit('HeaderBackButtonClick')

      if (this.$route.path.indexOf('/AddPaymentMethod') > 0) { // If the user is in the payment flow without a saved card, gets to manage payment, this will prevent a loop hitting back button
        this.$router.push({ name: 'MakePayment' }) // The user could have come from MakePayment or ReviewPayment, for now just default them back to MakePayment
      } else {
        window.history.length > 1 ? this.$router.go(-1) : this.$router.push({ name: 'Login' })
      }
    },

    openSidebar () {
      this.$store.commit('display/toggleSidebar')
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/colors';

.icon--hamburger {
  width: 1.875rem;
  height: 1.25rem;
  margin-bottom: 0.188rem;
}

.icon--hamburger ::v-deep .svg-fill {
  fill: colors.$white;

  /* Override only for CCC/ESM */
  .ccc & {
    fill: colors.$ccc--blue-2;
  }

  .esm & {
    fill: colors.$esm--green-3;
  }
}

.setup-header {
  width: 100%;
  height: 44px;
  height: calc(constant(safe-area-inset-top) + 44px); /* iOS 11.0 */
  height: calc(env(safe-area-inset-top) + 44px); /* iOS 11.2 */
  padding-top: constant(safe-area-inset-top); /* iOS 11.0 */
  padding-top: env(safe-area-inset-top); /* iOS 11.2 */
  white-space: nowrap;
}

.setup-header.prevent-click {
  pointer-events: none;
}

.text-header {
  line-height: 44px;
  color: colors.$white;

  /* Override only for CCC/ESM */
  .ccc & {
    color: colors.$ccc--blue-2;
  }

  .esm & {
    color: colors.$esm--green-3;
  }
}

.button--icon {
  height: 44px;
  margin: 0 15px;
}

#backButtonImage {
  width: 0.813rem;
  height: 0.813rem;
  margin-left: 0.188rem;
  border-color: colors.$white;
  border-style: solid;
  border-width: 0 0 3px 3px;
  transform: rotate(45deg);

  /* Override only for CCC/ESM */
  .ccc & {
    border-color: colors.$ccc--blue-2;
  }

  .esm & {
    border-color: colors.$esm--green-3;
  }
}
</style>

<i18n>
  {
    "en": {
      "open_menu": "Open Menu",
      "go_back": "Go Back"
    }
  }
</i18n>
